import {
    Button,
    ScrollArea,
    ScrollBar,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@repo/shadcn';
import {
    ColumnDef,
    PaginationOptions,
    PaginationState,
    SortingState,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';

import { TableLoader } from '../loader';

export type Payment = {
    id: string;
    amount: number;
    status: 'pending' | 'processing' | 'success' | 'failed';
    email: string;
};

interface DataTableProps<T> {
    data: T[];
    columns: ColumnDef<T>[];
    pagination?: PaginationState;
    sorting?: SortingState;
    isLoading?: boolean;
    paginationOptions?: Pick<
        PaginationOptions,
        'onPaginationChange' | 'rowCount'
    >;
}
export const DataTable: <T>(props: DataTableProps<T>) => JSX.Element = ({
    data,
    columns,
    pagination = {
        pageIndex: 0,
        pageSize: 5,
    },
    sorting,
    paginationOptions,
    isLoading = false,
}) => {
    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),

        manualPagination: true,
        manualSorting: true,
        ...paginationOptions,
        state: {
            pagination,
            sorting,
        },
    });

    const renderContent = () => {
        if (isLoading) {
            return <TableLoader columnLength={columns.length} />;
        }

        return table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, i) => (
                <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={`${i % 2 ? 'bg-gray-100' : 'bg-gray-50/80'}`}
                >
                    {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                            {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                            )}
                        </TableCell>
                    ))}
                </TableRow>
            ))
        ) : (
            <TableRow>
                <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                >
                    No results.
                </TableCell>
            </TableRow>
        );
    };

    return (
        <div className="h-auto w-full bg-white">
            <ScrollArea className="h-auto rounded-md border">
                <Table className="w-max min-w-full whitespace-nowrap bg-white">
                    <TableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                      header.column.columnDef
                                                          .header,
                                                      header.getContext(),
                                                  )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody className="h-full">{renderContent()}</TableBody>
                </Table>
                <ScrollBar orientation="horizontal" />
            </ScrollArea>
            {paginationOptions?.rowCount && (
                <div className="flex items-center justify-end space-x-2 py-2">
                    <div className="space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            Previous
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            Next
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};
